import { ReactNode, useEffect, useState } from 'react';
import paths, { LoginQueryParams } from '@constants/paths';
import BankIdModal from '@organisms/BankIdModal/BankIdModal';
import useCustomRouter from '@hooks/useCustomRouter';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { ErrorDataType, PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import logger from '@logger';
import { isAxiosError } from 'axios';
import { StyledCheckoutSubmitButton } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import { placeOrder } from '@api/interfaces/checkoutApi';
import { useAppDispatch } from '@hooks/useAppDispatch';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { setCompleted, setEditing, setErrorMessage, setSelectedMode } from '@slices/checkoutSlice';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import { BankIdSignContextProvider } from '@organisms/BankIdModal/BankIdSignContextProvider';
import useStore from '@hooks/useStore';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
  setError: (error: ErrorDataType) => void;
}

const InvoiceButton = ({ children, disabled = false, possiblyWrongStoreConfirmed, setError }: Props) => {
  const [showBankIdModal, setShowBankIdModal] = useState<boolean>(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const router = useCustomRouter();
  const checkCartConflict = useCheckCartConflict();
  const dispatch = useAppDispatch();
  const { store } = useStore();

  const openBankIdModal = () => {
    setShowBankIdModal(true);
  };

  const placeOrderHandler = async (orderRef = '') => {
    trackPlaceOrderClick();
    setPaymentInProgress(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });
      const response = await placeOrder({
        orderRef,
        selectedCardId: 'NONE',
        userClickedContinue:
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL ||
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY,
      });
      const { location } = response.headers;
      window.location.href = location;
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.response?.status === 400 && e.request.response === PlaceOrderErrorResponse.BANK_ID_PROMPT) {
          openBankIdModal();
        } else {
          if (e.response?.status === 401) {
            if (e.request.response === PlaceOrderErrorResponse.EMPTY_CART) {
              dispatch(setEmptyCartModalVisibility(true));
              router.push(paths.HOME);
            } else {
              // Make sure payment error modal is not shown, error handling is in interceptor
            }
          } else if (e.request.response === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
            dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
            dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
            dispatch(setErrorMessage(e.request.response));
            setError({ errorCode: e.request.response });
          } else {
            setError({ errorCode: e.request.response || 'default' });
            dispatch(setSelectedMode(''));
            logger.error({ error: 'invoice error', message: e.message });
          }

          setPaymentInProgress(false);
        }
      }
    }
  };

  useEffect(() => {
    if (router?.query?.from === LoginQueryParams.LOGIN_CONFIRM_SUCCESS) {
      placeOrderHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query?.from]);

  return (
    <>
      {showBankIdModal && (
        <BankIdSignContextProvider>
          <BankIdModal
            onClose={() => {
              setShowBankIdModal(false);
              setPaymentInProgress(false);
            }}
            onSuccess={async (orderRef) => {
              setShowBankIdModal(false);
              await placeOrderHandler(orderRef);
            }}
          />
        </BankIdSignContextProvider>
      )}
      <StyledCheckoutSubmitButton
        theme="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await placeOrderHandler();
          }
        }}
        type="submit"
        data-testid="invoice-checkout-button"
        disabled={disabled || paymentInProgress}
        isSubmitting={paymentInProgress}
      >
        {children}
      </StyledCheckoutSubmitButton>
    </>
  );
};

export default InvoiceButton;
